import React, { PropTypes } from 'react';
import styled from 'styled-components';
import { NitroButton } from 'nitro-components';
import CloseIcon from './close.svg';
import ServicenowApi from '../../api/ServicenowApi';
import AccidentTitle from '../../assets/images/accident-management/1800Accident_title.png';
import ClaimIcon from '../../assets/images/accident-management/icon-claim-management.jpg';
import FinanceIcon from '../../assets/images/accident-management/icon-finance-management.jpg';
import NotificationIcon from '../../assets/images/accident-management/icon-notifications.jpg';
import SpannerIcon from '../../assets/images/accident-management/icon-spanner-orange.jpg';

const Container = styled.div`
  text-align: center;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  width: 100%;
  padding: 36px 50px 0;
  top: 0;
  background: ${props => props.theme.color.white};
`;

const Close = styled.div`
  display: flex;
  align-items: center;
  position: absolute;
  right: 0;
  padding: 8px;
  margin-right: 16px;
  cursor: pointer;
`;

const Body = styled.div`
  padding: 80px 50px 86px;
`;

const SubmittedBody = styled.div`
  padding: 80px 50px 36px;
`;

const BodyText = styled.p`
  font-size: 14px;
`;

const Elements = styled.div`
  display: flex;
  padding-top: 24px;
  flex-wrap: wrap;
  justify-content: center;
`;

const Element = styled.div`
  flex-basis: 25%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  min-width: 120px;
  margin-bottom: 24px;
`;

const ElementImage = styled.img`
  margin-bottom: 10px;
  height: 40px;
`;

const ElementText = styled.span`
  font-size: 14px;
  color: ${props => props.theme.color.main};
  font-family: ${props => props.theme.font.bold};
`;

const Actions = styled.div`
  display: flex;
  justify-content: center;
  position: absolute;
  bottom: 0;
  width: 100%;
  background: white;
  padding-bottom: 36px;
`;

const Action = styled(NitroButton)`
  margin: 0 6px;
`;

const LearnMore = styled.a`
  display: flex;
  cursor: pointer;
  align-items: center;
  padding: 0 24px;
  margin: 0 6px;
  text-decoration: none;
  border: 1px solid ${props => props.theme.color.primary};
  color: ${props => props.theme.color.primary};
  border-radius: 4px;

  &:hover {
    color: ${props => props.theme.color.white};
    background: ${props => props.theme.color.primary};
  }
`;

const SubmittedHeader = styled(Header)`
  justify-content: start;
`;

const SubmittedHeaderText = styled.h3`
  color: ${props => props.theme.color.primary};
  font-family: ${props => props.theme.font.bold};
  margin: 0;
`;

const SubmittedBodyText = styled(BodyText)`
  text-align: left;
`;

export const ErrorMessage = styled.p`
  color: ${props => props.theme.color.negative};
  margin-bottom: 8px;
`;

class ServiceNow extends React.Component {
  state = {
    submittingInterest: false,
    submittedInterest: false,
    error: null
  };

  /**
   * Automatically close the dialog after a given period of time if it is still open.
   * @param ms Wait duration
   */
  automaticClose = ms => {
    setTimeout(() => {
      if (this.props.open) {
        this.props.handleClose();
      }
    }, ms || 5000);
  };

  /**
   * Submits a request for the user to register interest in service now.
   */
  submitInterest = () => {
    this.setState({ submittingInterest: true });

    ServicenowApi.submitInterest()
      .then(() => {
        this.setState({ submittedInterest: true });
        this.automaticClose();
      })
      .catch(() => {
        this.setState({
          error:
            'Oops there seems to be an issue, please refresh the page and try again'
        });
      })
      .finally(() => {
        this.setState({ submittingInterest: false });
      });
  };

  render() {
    const { handleClose } = this.props;
    const { error, submittingInterest, submittedInterest } = this.state;

    if (submittedInterest) {
      return (
        <container>
          <SubmittedHeader>
            <SubmittedHeaderText>
              Thank you for your submission
            </SubmittedHeaderText>
            <Close onClick={handleClose}>
              <img src={CloseIcon} alt="close icon" />
            </Close>
          </SubmittedHeader>
          <SubmittedBody>
            <SubmittedBodyText>
              Your Relationship Manager will be in touch with you shortly.
            </SubmittedBodyText>
          </SubmittedBody>
        </container>
      );
    }

    return (
      <Container>
        <Header>
          <img src={AccidentTitle} alt="1800 Accident" />
          <Close onClick={handleClose}>
            <img src={CloseIcon} alt="close icon" />
          </Close>
        </Header>
        {error ? <ErrorMessage>{error}</ErrorMessage> : null}
        <Body>
          <BodyText>
            1800Accident solution ensures effective accident management,
            increasing overall fleet productivity, reducing accident related
            expenses and identifying areas of risk to target driver safety
            programs. We take the hassle out of claims management, our tailored
            solution encompasses four core elements.
          </BodyText>
          <Elements>
            <Element>
              <ElementImage src={ClaimIcon} alt="Claim Management" />
              <ElementText>Claim Management</ElementText>
            </Element>
            <Element>
              <ElementImage src={SpannerIcon} alt="Claim Management" />
              <ElementText>Repair Management</ElementText>
            </Element>
            <Element>
              <ElementImage src={FinanceIcon} alt="Claim Management" />
              <ElementText>Finance Management</ElementText>
            </Element>
            <Element>
              <ElementImage src={NotificationIcon} alt="Claim Management" />
              <ElementText>Notifications and Reporting</ElementText>
            </Element>
          </Elements>
          <BodyText>
            To learn more about this service, click on ‘Learn more’ or if you
            are interested in this service, click ‘Submit Interest’ to be
            contacted by your Relationship Manager.
          </BodyText>
        </Body>
        <Actions>
          <LearnMore href="/legal/1800Accident_Flyer.pdf" target="_blank">
            Learn More
          </LearnMore>
          <Action
            label="Submit Interest"
            size="small"
            variant="primary"
            loading={submittingInterest}
            disabled={submittingInterest}
            action={this.submitInterest}
          />
        </Actions>
      </Container>
    );
  }
}

ServiceNow.propType = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired
};

export default ServiceNow;



// WEBPACK FOOTER //
// ./src/features/Servicenow/ServiceNow.js